/*
 * Hero carousel.
 *
 * Replaces Dawn's <slideshow-component>. That class works for a single slide
 * but with several it never applied a scroll position here, and attaching a
 * scroll listener alongside it froze the renderer — it sets
 * enableSliderLooping and writes scrollLeft from inside its own scroll
 * handler, which feeds back on itself. Its initialisation was also
 * inconsistent between loads (`slider` undefined on some).
 *
 * The markup and class names are unchanged, so the existing CSS — including
 * the autoplay progress fill on the active dot — still applies.
 */
class HeroCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.hero__slider');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('.hero__slide'));
    this.dots = Array.from(this.querySelectorAll('.hero__dot'));
    this.prev = this.querySelector('.slider-button--prev');
    this.next = this.querySelector('.slider-button--next');
    if (this.slides.length < 2) return;

    this.index = 0;
    this.painted = -1;
    this.dragging = false;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
    if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.index - 1));
    if (this.next) this.next.addEventListener('click', () => this.goTo(this.index + 1));

    this.track.addEventListener('scroll', () => this.scheduleSync(), { passive: true });
    this.sync();
    this.setupDrag();
    this.setupAutoplay();
    this.setupResize();
  }

  /*
   * Slides are 100% of the track. scrollLeft is in px, so a live resize
   * leaves the viewport parked between slides — mid widths read as a blank
   * bag. Re-snap the current index without a smooth scroll.
   */
  setupResize() {
    this.onResize = () => {
      if (this.resizeTick) return;
      this.resizeTick = requestAnimationFrame(() => {
        this.resizeTick = null;
        if (!this.slides.length) return;
        const left = this.slides[this.index].offsetLeft - this.slides[0].offsetLeft;
        this.track.scrollTo({ left, behavior: 'instant' });
      });
    };
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.track);
  }

  /*
   * Pointer dragging, so the whole slide can be grabbed. Touch devices would
   * scroll the track natively, but `touch-action: pan-y` hands horizontal
   * gestures here instead — one code path for mouse, pen and touch.
   */
  setupDrag() {
    const track = this.track;
    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let travelled = 0;
    let dragging = false;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      /*
       * The previous drag may still be settling. Cancel it: if its `settled`
       * ran inside this drag it would restore snap and smooth scrolling
       * underneath us, and the track would fight every pointermove.
       */
      this.cancelSettle();
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      travelled = 0;
      dragging = false;
      lastX = event.clientX;
      lastT = performance.now();
      velocity = 0;
      this.stop();
    });

    /*
     * The 6px threshold is crossed well after the browser has begun a
     * selection drag, and `user-select: none` arriving at that point does not
     * stop a gesture already in flight. Refuse the selection outright while a
     * pointer is down on the track.
     */
    track.addEventListener('selectstart', (event) => {
      if (pointerId !== null) event.preventDefault();
    });

    track.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      let delta = event.clientX - startX;
      const now = performance.now();
      const dt = now - lastT;
      if (dt > 48) {
        velocity = 0;
      } else if (dt > 0) {
        velocity = velocity * 0.55 + ((event.clientX - lastX) / dt) * 0.45;
      }
      lastX = event.clientX;
      lastT = now;

      // Only take over past a small threshold, so taps and button clicks
      // still behave normally.
      if (!dragging) {
        if (Math.abs(delta) < 6) return;
        dragging = true;
        this.dragging = true;
        track.classList.add('is-dragging');
        /*
         * Re-baseline here rather than at pointerdown. Grabbing during goTo's
         * smooth scroll leaves the pointerdown reading stale by however far
         * the animation travelled in between, and the drag opens with a jump.
         * scrollTo to the current position cancels that animation; the class
         * above has already turned scroll-behavior off, so this is the
         * position the drag genuinely starts from.
         */
        track.scrollTo({ left: track.scrollLeft, behavior: 'instant' });
        startX = event.clientX;
        startScroll = track.scrollLeft;
        delta = 0;
        // Drop anything the browser managed to select on the way in.
        const selection = window.getSelection();
        if (selection && !selection.isCollapsed) selection.removeAllRanges();
        try {
          track.setPointerCapture(pointerId);
        } catch (e) {
          /* capture is best-effort */
        }
      }

      travelled = delta;
      track.scrollLeft = startScroll - delta;
      event.preventDefault();
    });

    const release = () => {
      if (pointerId === null) return;

      if (dragging) {
        try {
          track.releasePointerCapture(pointerId);
        } catch (e) {
          /* already released */
        }

        /*
         * Full-bleed slides made a 15% distance gate ~200px on desktop — a
         * flick never cleared it and snapped back. Commit on a short flick
         * (velocity) or a modest drag, capped in px so desktop stays light.
         */
        const width = this.slides[0].getBoundingClientRect().width || 1;
        const raw = track.scrollLeft / width;
        if (performance.now() - lastT > 80) velocity = 0;

        const absTravel = Math.abs(travelled);
        const flicked = Math.abs(velocity) > 0.35 && absTravel > 24;
        const dragged = absTravel > Math.min(width * 0.12, 64);
        const towardNext = flicked ? velocity < 0 : travelled < 0;
        const index =
          flicked || dragged
            ? towardNext
              ? Math.ceil(raw)
              : Math.floor(raw)
            : Math.round(raw);
        this.goTo(Math.max(0, Math.min(this.slides.length - 1, index)));

        /*
         * Swallow the click a drag fires on release. This is time-boxed
         * rather than a `once` listener: a drag that ends without a following
         * click would leave a `once` listener armed, and it would then eat
         * the next genuine click — the CTA on the slide, for instance.
         */
        this.suppressClicksUntil = performance.now() + 250;
      }

      pointerId = null;
      dragging = false;
      this.dragging = false;

      /*
       * Covers both exits. After a real drag this lets the settle land. After
       * a tap it re-arms the settle that pointerdown cancelled — otherwise
       * `is-dragging` would be left on the track with nothing to remove it,
       * and snap would stay off for good.
       */
      if (track.classList.contains('is-dragging')) this.armSettle(track);

      /*
       * Only hand autoplay back if the pointer has left the hero. mouseenter
       * fired before the drag and will not fire again on release, so resuming
       * unconditionally advances the slide under a resting cursor.
       */
      if (!this.matches(':hover')) this.play();
    };

    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);
    // Stop the browser's native image/link drag from hijacking the gesture.
    track.addEventListener('dragstart', (event) => event.preventDefault());

    this.suppressClicksUntil = 0;
    track.addEventListener(
      'click',
      (event) => {
        if (performance.now() >= this.suppressClicksUntil) return;
        event.stopPropagation();
        event.preventDefault();
      },
      true
    );
  }

  /*
   * `is-dragging` stays on until the settle finishes. Dropping it at release
   * would restore `scroll-snap-type: mandatory` mid-flight, and the browser
   * would snap the part-dragged position back to the previous slide before
   * goTo's scroll had a chance to land.
   *
   * Both the scrollend listener and the fallback timer are held so the next
   * pointerdown can cancel them. Without that, a settle armed by one drag
   * fires inside the next one and strips `is-dragging` while it is still
   * needed — snap yanks the track back and the drag goes dead.
   */
  armSettle(track) {
    this.cancelSettle();
    const settled = () => {
      this.cancelSettle();
      if (!this.dragging) track.classList.remove('is-dragging');
    };
    this.settleHandler = settled;
    this.settleTrack = track;
    if ('onscrollend' in window) {
      track.addEventListener('scrollend', settled, { once: true });
      this.settleTimer = setTimeout(settled, 700); // if scrollend never fires
    } else {
      this.settleTimer = setTimeout(settled, 400);
    }
  }

  cancelSettle() {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = null;
    if (this.settleHandler && this.settleTrack) {
      this.settleTrack.removeEventListener('scrollend', this.settleHandler);
    }
    this.settleHandler = null;
  }

  disconnectedCallback() {
    this.stop();
    this.cancelSettle();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resizeTick) cancelAnimationFrame(this.resizeTick);
  }

  goTo(index) {
    const last = this.slides.length - 1;
    // Wrap, so prev from the first slide lands on the last.
    const target = index < 0 ? last : index > last ? 0 : index;
    const left = this.slides[target].offsetLeft - this.slides[0].offsetLeft;
    // 'instant', not 'auto': 'auto' defers to the CSS `scroll-behavior: smooth`
    // on the track, so reduced-motion users would still get an animation.
    this.track.scrollTo({ left, behavior: this.reduceMotion.matches ? 'instant' : 'smooth' });
    this.index = target;
    this.paint();
  }

  scheduleSync() {
    if (this.pending) return;
    this.pending = requestAnimationFrame(() => {
      this.pending = null;
      this.sync();
    });
  }

  /* Whichever slide sits nearest the scroll position owns the active dot. */
  sync() {
    const origin = this.slides[0].offsetLeft;
    const position = this.track.scrollLeft;

    let nearest = 0;
    let best = Infinity;
    this.slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - origin - position);
      if (distance < best) {
        best = distance;
        nearest = i;
      }
    });

    this.index = nearest;
    this.paint();
  }

  paint() {
    /*
     * sync() runs off every scroll frame, and toggling `inert` invalidates
     * style and rebuilds the accessibility tree each time. Nothing here
     * changes unless the active index does, so leave early when it has not —
     * this is what makes dragging and the smooth settle run clean.
     */
    if (this.painted === this.index) return;
    this.painted = this.index;

    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('hero__dot--active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    this.slides.forEach((slide, i) => {
      // Keep off-screen slides out of the tab order and the a11y tree.
      slide.toggleAttribute('inert', i !== this.index);
      slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
    });
  }

  setupAutoplay() {
    const hero = this.closest('.hero');
    if (!hero || !hero.classList.contains('hero--autoplay')) return;

    const raw = getComputedStyle(hero).getPropertyValue('--hero-autoplay-speed').trim();
    this.interval = (parseFloat(raw) || 6) * 1000;
    if (this.reduceMotion.matches) return;

    this.play();
    ['mouseenter', 'focusin'].forEach((e) => this.addEventListener(e, () => this.stop()));
    ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.play()));
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.stop() : this.play();
    });
  }

  play() {
    // Guard: without autoplay `interval` is undefined, and setInterval would
    // then fire on every tick.
    if (!this.interval) return;
    /*
     * A drag that wanders outside the hero fires mouseleave, which would
     * otherwise restart autoplay and advance the slide under the pointer.
     */
    if (this.dragging) return;
    this.stop();
    this.timer = setInterval(() => this.goTo(this.index + 1), this.interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}

customElements.define('hero-carousel', HeroCarousel);
